import React, { createContext, useCallback, useContext, useState } from "react"
import { Product } from "../types/Product"
import { fetch } from "cross-fetch"
import { createGlobalStyle } from "styled-components"
import { toast } from "react-toastify"
// @ts-ignore
import checkIconUrl from "../icons/ic-check.svg"
import { EventContext } from "./EventContext"
import { useTranslation } from "react-i18next"

type ProductsList = { [boothSlug: string]: string[] }

type SubmissionState = "submitting" | "submitted" | "failed" | undefined

type SubmissionInput = {
  name: string
  lastName: string
  company?: string
  email: string
  acceptsDataTerms: boolean
  sendProductFactSheet: boolean
  event: {
    slug: string
    code?: string
  }
}

type ContextType = {
  toggleProduct: (product: Product, productNameForToast?: string) => any
  products: ProductsList
  setProducts: (products: ProductsList) => any
  reset: () => any
  submissionState: SubmissionState
  submit: (value: SubmissionInput) => any
  disabled?: boolean
}

const InterestsContext = createContext<ContextType>({
  products: {},
  setProducts: () => null,
  toggleProduct: () => null,
  reset: () => {},
  submissionState: undefined,
  submit: () => null,
  disabled: false,
})

export const useInterests = () => {
  return useContext(InterestsContext)
}

const Toast: React.FC<{ productName: string }> = ({ productName }) => {
  const { t } = useTranslation()
  return (
    <>
      <img src={checkIconUrl} style={{ marginRight: 8 }} />
      {t("productWasAddedToYourRequests", { productName })}
    </>
  )
}
const showToast = (productName: string | undefined) =>
  productName && toast(<Toast productName={productName} />)

const WhileSubmittingGlobalStyles = createGlobalStyle`
html, body {pointer-events: none; cursor: wait}`

export const WithProductInterestsContext: React.FC = props => {
  const [products, setProductsList] = useState<ProductsList>({})

  const toggleProduct = useCallback(
    (p: Product, productNameForToast: string | undefined = undefined) =>
      setProductsList(list => {
        // If there's not any product in the list, just add it
        if (!list[p.parentBoothSlug]) {
          showToast(productNameForToast)
          return {
            ...list,
            [p.parentBoothSlug]: [p.slug],
          }
          // Product's in there - remove
        } else if (list[p.parentBoothSlug].includes(p.slug)) {
          return {
            ...list,
            [p.parentBoothSlug]: list[p.parentBoothSlug].filter(
              s => s !== p.slug
            ),
          }
          // Add it
        } else {
          showToast(productNameForToast)
          return {
            ...list,
            [p.parentBoothSlug]: [...list[p.parentBoothSlug], p.slug],
          }
        }
      }),
    [setProductsList]
  )

  const [submissionState, _setSubmissionState] = useState<SubmissionState>()

  const submit = useCallback(
    (value: SubmissionInput) => {
      console.log("values =>", value)

      _setSubmissionState("submitting")
      return fetch("/.netlify/functions/checkout", {
        method: "POST",
        body: JSON.stringify({
          ...value,
          sendProductFactSheet:
            Array.isArray(value.sendProductFactSheet) &&
            value.sendProductFactSheet[0] === "on"
              ? true
              : value.sendProductFactSheet,
          acceptsDataTerms:
            Array.isArray(value.acceptsDataTerms) &&
            value.acceptsDataTerms[0] === "on"
              ? true
              : value.acceptsDataTerms,
          products,
        }),
      })
        .then(res => {
          if (res.status === 200) return res.json()
          throw res.status
        })
        .then(() => _setSubmissionState("submitted"))
        .catch(code => {
          console.log(code)
          _setSubmissionState("failed")
        })
    },
    [products]
  )

  const reset = useCallback(() => {
    setProductsList({})
    _setSubmissionState(undefined)
  }, [setProductsList])

  const { data } = useContext(EventContext)
  return (
    <InterestsContext.Provider
      value={{
        toggleProduct,
        products,
        reset,
        setProducts: setProductsList,
        submit,
        submissionState,
        disabled: data?.disableInformationRequest || false,
      }}
    >
      {submissionState === "submitting" && <WhileSubmittingGlobalStyles />}
      {props.children}
    </InterestsContext.Provider>
  )
}
